import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

export const getActionName = (isArchive: boolean, isBulk: boolean = false): string =>
	isArchive ? (isBulk ? 'bulkArchive' : 'archive') : isBulk ? 'bulkRestore' : 'restore';

// Analytics Event Types:
export const UI_EVENT_TYPE = 'sendUIEvent';
export const TRACK_EVENT_TYPE = 'sendTrackEvent';

// Analytics Event Action Subjects:
export const OPTION_SELECT_ACTION_SUBJECT = 'selectDropdown';
export const SOURCE_SPACE_SELECT_ACTION_SUBJECT = 'spaceSelect';
export const BUTTON_ACTION_SUBJECT = 'button';

// Event parameters - 'createAnalyticsEvent', 'type', 'action', and 'actionSubject' are required,
// everything else is optional
export type AnalyticEventProps = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	type: string;
	action: string;
	actionSubject: string;
	actionSubjectId?: string | null;
	name: string;
	source?: string;
	attributes?: object;
};

export type AnalyticesEventContainerProps = {
	containerType?: string;
	containerId?: string | null;
};

// For functions like 'fireUIEvent()' where the 'type' is fixed
export type FixedTypeEventProps = Omit<AnalyticEventProps, 'type'>;

// *** Fires analytics UI Events
export type UIEventProps = Omit<FixedTypeEventProps, 'name'>;
export const fireUIEvent = (eventProps: UIEventProps) =>
	fireAnalyticEvent({ ...eventProps, type: UI_EVENT_TYPE });

// For UI events where the action is "clicked", which is very common
export type UIClickEventProps = Omit<UIEventProps, 'action'>;
export const fireUIClickEvent = (eventProps: UIClickEventProps) =>
	fireUIEvent({ ...eventProps, action: 'clicked' });

// *** Fires analytics Track Events
// (Track events currently have the same properties as UI events)
export type TrackEventProps = UIEventProps & AnalyticesEventContainerProps;
export const fireTrackEvent = (eventProps: TrackEventProps) =>
	fireAnalyticEvent({ ...eventProps, type: TRACK_EVENT_TYPE });

// *** Fires analytics Screen Events
// Screen events must have a "name" and can only have attributes
export type ScreenEventProps = Pick<
	AnalyticEventProps,
	'createAnalyticsEvent' | 'name' | 'attributes'
>;
export const fireScreenEvent = (eventProps: ScreenEventProps) =>
	fireAnalyticEvent({ ...eventProps, type: 'sendScreenEvent' });

// For generic events, 'createAnalyticsEvent' and 'type' are required and everything else is optional
type RequireOnly<T, K extends keyof T> = Pick<T, K> & Partial<Omit<T, K>>;

export const fireAnalyticEvent = (
	params: RequireOnly<AnalyticEventProps, 'createAnalyticsEvent' | 'type'>,
) => {
	const { createAnalyticsEvent, type, ...data } = params;
	createAnalyticsEvent({
		type,
		data,
	}).fire();
};
